import { syncService } from '../../services/syncService';
import { SyncStatus } from '../../types/sync/enums';
import { TranslationKey } from '../../i18n/translationKeys';
import { useTenantStore } from '../../stores/tenantStore';
import type { SyncStatusResponse } from '../../types/sync/syncStatusResponse';

// Fallback guard: abort a stream that never sends a terminal snapshot or deadline.
const FALLBACK_TIMEOUT_MS = 20 * 60 * 1000;
// Buffer past the saga's deadline before the frontend gives up.
const DEADLINE_BUFFER_MS = 30_000;

type Translate = (key: string, opts?: Record<string, string>) => string;

const activeCompany = (): string => {
    const { ciu, tenantCode } = useTenantStore.getState();
    return `${ciu}:${tenantCode}`;
};

/**
 * Streams sync progress snapshots into the provided setters until a terminal snapshot, an abort, or
 * the deadline-aware fallback timeout. Shared by {@link useSync} (fresh trigger) and
 * {@link useResumeSync} (reattaching to an already-running sync) so the streaming/timeout logic lives
 * in one place. Never rejects — terminal state is reported through <c>setSyncStatus</c>.
 *
 * The stream is bound to the company that was on the request when it opened — the backend scopes
 * the progress channel on its headers and the connection outlives any later company switch. So the
 * active company is re-checked per snapshot and the stream dropped once it changes, otherwise the
 * previous company's progress keeps painting the new company's UI.
 */
export async function consumeSyncStream(
    controller: AbortController,
    setProgress: (snapshot: SyncStatusResponse) => void,
    setSyncStatus: (status: string | null) => void,
    t: Translate,
): Promise<void> {
    const openedForCompany = activeCompany();
    let timedOut = false;
    let timeoutId = setTimeout(() => { timedOut = true; controller.abort(); }, FALLBACK_TIMEOUT_MS);
    try {
        for await (const snapshot of syncService.streamProgress(controller.signal)) {
            // Silent: the snapshot belongs to the company the user just left, so neither its progress
            // nor a terminal message is theirs to see.
            if (activeCompany() !== openedForCompany) { controller.abort(); return; }
            setProgress(snapshot);
            if (snapshot.deadline) {
                const remaining = new Date(snapshot.deadline).getTime() - Date.now() + DEADLINE_BUFFER_MS;
                clearTimeout(timeoutId);
                timedOut = false;
                timeoutId = setTimeout(() => { timedOut = true; controller.abort(); }, Math.max(remaining, 0));
            }
            if (snapshot.status === SyncStatus.Completed) { setSyncStatus(t(TranslationKey.Settings.SyncCompleted)); return; }
            if (snapshot.status === SyncStatus.Failed) { setSyncStatus(t(TranslationKey.Settings.SyncFailed)); return; }
        }
    } catch (e) {
        if ((e as Error).name === 'AbortError') {
            if (timedOut) setSyncStatus(t(TranslationKey.Settings.SyncTimedOut));
            // otherwise aborted by a newer trigger — stay silent
        } else {
            setSyncStatus(t(TranslationKey.Settings.SyncError, { message: (e as Error).message }));
        }
    } finally {
        clearTimeout(timeoutId);
    }
}
